/**
 * Billy — brand mark + wordmark.
 *
 * The icon is a stylised "billy can" (the Aussie camp tin kettle with a bail
 * handle) with a checkmark inside it and two wisps of steam rising off the top.
 * Simple visual pun on the app name, fits the existing Swiss / high-contrast
 * black-on-white design system.
 */

/**
 * Icon-only square tile. bg = tile colour, fg = stroke colour.
 * Defaults to black tile + white marks (matches the existing header style).
 */
export const BillyMark = ({
  size = 40,
  bg = "#000",
  fg = "#fff",
  className = "",
  ...rest
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Billy"
    className={className}
    {...rest}
  >
    <rect width="64" height="64" fill={bg} />
    {/* steam */}
    <path
      d="M23 22 C 20 18, 27 15, 24 10"
      stroke={fg}
      strokeWidth="2.25"
      strokeLinecap="round"
      fill="none"
      opacity="0.8"
    />
    <path
      d="M34 22 C 31 17, 38 14, 35 8"
      stroke={fg}
      strokeWidth="2.25"
      strokeLinecap="round"
      fill="none"
      opacity="0.8"
    />
    {/* bail handle (wire handle of the billy) */}
    <path
      d="M18 30 Q 32 18, 46 30"
      stroke={fg}
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
    {/* rim */}
    <line
      x1="14"
      y1="30"
      x2="50"
      y2="30"
      stroke={fg}
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    {/* can body */}
    <rect
      x="17"
      y="30"
      width="30"
      height="26"
      rx="1"
      stroke={fg}
      strokeWidth="2.5"
      fill="none"
    />
    {/* tick — "paid" */}
    <path
      d="M23 43 L 29 49 L 42 35"
      stroke={fg}
      strokeWidth="3.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

/**
 * Icon + wordmark. Compact horizontal lockup used in the header and login page.
 * `subtitle` is the business tagline rendered in small caps beneath the word.
 */
export const BillyLogo = ({
  size = 40,
  subtitle = "The Outdoor Blind Company",
  tone = "dark", // 'dark' = black tile / black wordmark on white bg; 'light' = white tile on dark bg
  compact = false, // when true, shows only the word (no subtitle)
  className = "",
}) => {
  const bg = tone === "dark" ? "#000" : "#fff";
  const fg = tone === "dark" ? "#fff" : "#000";
  const textColor = tone === "dark" ? "text-black" : "text-white";
  const subColor = tone === "dark" ? "text-neutral-500" : "text-neutral-300";

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <BillyMark size={size} bg={bg} fg={fg} />
      <div className="leading-none">
        <div
          className={`font-display font-black italic text-[20px] tracking-tight ${textColor}`}
          style={{ letterSpacing: "-0.02em" }}
        >
          Billy<span className="text-red-600 not-italic">.</span>
          <span className="not-italic font-bold text-[14px] ml-1.5 tracking-wide opacity-80">
            Custom
          </span>
        </div>
        {!compact && subtitle && (
          <div
            className={`text-[10px] uppercase tracking-[0.25em] ${subColor} mt-1`}
          >
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillyLogo;
